import { ArrowPathIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { daysAgo } from "@sp-crm/core";
import {
    AI_SUMMARY_FEEDBACK_OPTIONS,
    AiSummaryState,
} from "components/ai-summary/ai-summary-state";
import {
    InlineFeedbackButtons,
    InlineFeedbackForm,
} from "components/feedback/inline-feedback";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { ClampedText } from "components/ui/clamped-text";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useEffect, useState } from "react";
import { breakNewlines } from "util/text";

interface AiSummaryBannerProps {
    aiSummary: AiSummaryState;
    className: string;
}

interface EditSummaryProps {
    initialText: string;
    onSave: (text: string) => Promise<void>;
    onCancel: () => void;
}

const EditSummary: React.FC<EditSummaryProps> = props => {
    const { initialText, onSave, onCancel } = props;
    const [text, setText] = useState(initialText);
    const [isSaving, setIsSaving] = useState(false);

    const submit = useCallback(async () => {
        setIsSaving(true);
        await onSave(text);
        setIsSaving(false);
    }, [text, onSave]);

    return (
        <div className="mt-2 space-y-2">
            <TextArea
                rows={4}
                autoGrow
                value={text}
                onChange={v => setText(v.target.value)}
            />
            <div className="flex justify-end space-x-2">
                <SecondaryButton disabled={isSaving} onClick={onCancel}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={isSaving} onClick={submit}>
                    {isSaving ? <Spinner /> : "Save"}
                </PrimaryButton>
            </div>
        </div>
    );
};

const summaryProvenance = (summary: NonNullable<AiSummaryState["summary"]>): string => {
    const generated = `Generated ${daysAgo(summary.dateAdded)}`;
    if (!summary.editedAt) {
        return generated;
    }
    return `${generated} · Edited by ${summary.editedByName} on ${daysAgo(
        summary.editedAt,
    )}`;
};

export const AiSummaryBanner: React.FC<AiSummaryBannerProps> = props => {
    const { aiSummary, className } = props;

    const {
        summary,
        isLoading,
        isGenerating,
        isRemoving,
        isSubmittingFeedback,
        error,
        feedbackGiven,
        showRemoveButton,
        generate,
        updateText,
        submitPositiveFeedback,
        startNegativeFeedback,
        submitNegativeFeedback,
        removeSummary,
    } = aiSummary;

    const [editing, setEditing] = useState(false);
    const startEditing = useCallback(() => setEditing(true), []);
    const stopEditing = useCallback(() => setEditing(false), []);
    const saveText = useCallback(
        async (text: string) => {
            const saved = await updateText(text);
            if (saved) {
                setEditing(false);
            }
        },
        [updateText],
    );

    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const openFeedbackForm = useCallback(() => {
        setShowFeedbackForm(true);
        startNegativeFeedback();
    }, [startNegativeFeedback]);
    const sendFeedback = useCallback(
        (combinedText: string | null) => {
            submitNegativeFeedback(combinedText);
            setShowFeedbackForm(false);
        },
        [submitNegativeFeedback],
    );

    useEffect(() => {
        if (feedbackGiven !== "negative") {
            setShowFeedbackForm(false);
        }
    }, [feedbackGiven]);

    if (!summary) {
        if (!error) {
            return null;
        }

        return (
            <div className={className}>
                <p className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</p>
            </div>
        );
    }

    return (
        <div className={className}>
            <div className="rounded-md bg-gray-50 px-3 pb-2 pt-3 ring-1 ring-inset ring-gray-200">
                <div className="flex items-start gap-2">
                    <SparklesIcon
                        aria-hidden="true"
                        className="mt-0.5 size-5 shrink-0 text-gray-400"
                    />
                    <div className="min-w-0 flex-1">
                        {error ? (
                            <p className="mb-2 rounded-md bg-red-50 p-2 text-sm text-red-600">
                                {error}
                            </p>
                        ) : null}
                        {editing ? (
                            <>
                                <h3 className="twoverride text-sm font-medium text-gray-600">
                                    AI Summary
                                </h3>
                                <EditSummary
                                    initialText={summary.text}
                                    onSave={saveText}
                                    onCancel={stopEditing}
                                />
                            </>
                        ) : (
                            <>
                                <div className="text-sm text-gray-700">
                                    <ClampedText lines={2}>
                                        <h3 className="twoverride inline text-sm font-medium text-gray-600">
                                            AI Summary:
                                        </h3>{" "}
                                        {breakNewlines(summary.text)}
                                    </ClampedText>
                                </div>
                                <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                                    <p className="text-xs text-gray-500">
                                        {summaryProvenance(summary)}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <InlineFeedbackButtons
                                            submitted={feedbackGiven}
                                            submitting={isSubmittingFeedback}
                                            positiveAriaLabel="This summary is helpful"
                                            negativeAriaLabel="This summary needs improvement"
                                            onPositive={submitPositiveFeedback}
                                            onNegative={openFeedbackForm}
                                        />
                                        <SecondaryCompactButton
                                            onClick={generate}
                                            disabled={isLoading}
                                            className="items-center space-x-1">
                                            <ArrowPathIcon
                                                aria-hidden="true"
                                                className={`h-3.5 w-3.5 ${
                                                    isGenerating ? "animate-spin" : ""
                                                }`}
                                            />
                                            <span>Regenerate</span>
                                        </SecondaryCompactButton>
                                        {showRemoveButton ? (
                                            <DeleteButton
                                                size="sm"
                                                backgroundColor="bg-gray-100"
                                                disabled={isRemoving}
                                                onClick={removeSummary}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                                {showFeedbackForm ? (
                                    <div className="mt-2 rounded-md bg-white p-4 ring-1 ring-inset ring-gray-200">
                                        <InlineFeedbackForm
                                            prompt="What could be improved?"
                                            options={AI_SUMMARY_FEEDBACK_OPTIONS}
                                            submitting={isSubmittingFeedback}
                                            onSubmit={sendFeedback}
                                        />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                    {editing ? null : (
                        <EditButton
                            size="sm"
                            backgroundColor="bg-gray-100"
                            onClick={startEditing}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
